import { Routes } from '@angular/router';
import { AdminGuard } from './core/guards/admin.guard';
import { CourierGuard } from './core/guards/courier.guard';
import { PRIVATE_ROUTES, PUBLIC_ROUTES } from './core/constants/app.constants';
import {
  FORCE_CHANGE_PASSWORD_ROUTE,
  MustChangePasswordGuard,
} from './core/guards/must-change-password.guard';
import { PrivateWithRoleGuard } from './core/guards/private-with-role.guard';
import { ReceptionistBlockedGuard } from './core/guards/receptionist-blocked.guard';
import { ClinicStaffGuard } from './core/guards/clinic-staff.guard';
import { VisitorOnlyGuard } from './core/guards/visitor-only.guard';
import { PublicGuard } from './core/guards/public.guard';
import { SpecificSignInGuard } from './core/guards/specific-sign-in.guard';
import { SubscriptionGuard } from './core/guards/subscription.guard';
import { TagClaimerGuard } from './core/guards/tag-claimer.guard';

export const routes: Routes = [
    {
        path: PUBLIC_ROUTES.login,
        canActivate: [PublicGuard],
        loadComponent: () => import('./auth/login/login.component').then(m => m.LoginComponent),
    },
    {
        path: PUBLIC_ROUTES.signIn,
        canActivate: [PublicGuard],
        loadComponent: () => import('./auth/sign-in/sign-in.component').then(m => m.SignInComponent),
    },
    {
        path: PUBLIC_ROUTES.forgotPassword,
        canActivate: [PublicGuard],
        loadComponent: () => import('./auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
    },
    {
        path: PUBLIC_ROUTES.resetPassword,
        canActivate: [PublicGuard],
        loadComponent: () => import('./auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
    },
    {
        path: FORCE_CHANGE_PASSWORD_ROUTE,
        canActivate: [MustChangePasswordGuard],
        loadComponent: () => import('./auth/force-change-password/force-change-password.component').then(m => m.ForceChangePasswordComponent),
    },
    {
        path: PRIVATE_ROUTES.specificSignIn,
        canActivate: [SpecificSignInGuard],
        loadComponent: () => import('./auth/specific-sign-in/specific-sign-in.component').then(m => m.SpecificSignInComponent),
    },
    {
        // QualiPass NFC tap landing: intentionally unguarded (serves logged-in and
        // logged-out users); the component routes to check-in or login itself.
        path: PUBLIC_ROUTES.tap,
        loadComponent: () => import('./pages/tap/tap-landing.component').then(m => m.TapLandingComponent),
    },
    {
        // Courier (REPARTO) self sign-up, reached from the tap landing, and their home page.
        path: PUBLIC_ROUTES.courierSignUp,
        canActivate: [PublicGuard],
        loadComponent: () => import('./pages/courier/courier-sign-up/courier-sign-up.component').then(m => m.CourierSignUpComponent),
    },
    {
        path: PRIVATE_ROUTES.courierHome,
        canActivate: [MustChangePasswordGuard, CourierGuard],
        loadComponent: () => import('./pages/courier/courier-home/courier-home.component').then(m => m.CourierHomeComponent),
    },
    {
        path: PUBLIC_ROUTES.tapInvalid,
        loadComponent: () => import('./pages/tap/tap-invalid.component').then(m => m.TapInvalidComponent),
    },
    {
        // Unguarded like /tap: the reminder arrives by email and the visitor may not be
        // signed in on the device that opens it. The token in the link is the authorisation.
        path: PUBLIC_ROUTES.tapCheckout,
        loadComponent: () => import('./pages/tap/tap-checkout.component').then(m => m.TapCheckoutComponent),
    },
    {
        path: PRIVATE_ROUTES.checkIn,
        canActivate: [MustChangePasswordGuard, PrivateWithRoleGuard],
        loadComponent: () => import('./pages/check-in/check-in.component').then(m => m.CheckInComponent),
    },
    {
        path: PRIVATE_ROUTES.claimTag,
        canActivate: [MustChangePasswordGuard, TagClaimerGuard],
        loadComponent: () => import('./pages/qualipass-claim/qualipass-claim.component').then(m => m.QualiPassClaimComponent),
    },
    {
        path: PRIVATE_ROUTES.clinicToday,
        canActivate: [MustChangePasswordGuard, ClinicStaffGuard],
        loadComponent: () => import('./pages/clinic-today/clinic-today.component').then(m => m.ClinicTodayComponent),
    },
    {
        path: PRIVATE_ROUTES.myVisits,
        canActivate: [MustChangePasswordGuard, VisitorOnlyGuard],
        loadComponent: () => import('./pages/my-visits/my-visits.component').then(m => m.MyVisitsComponent),
    },
    {
        path: PRIVATE_ROUTES.subscription,
        canActivate: [SubscriptionGuard],
        loadComponent: () => import('./pages/subscription/subscription.component').then(m => m.SubscriptionComponent)
    },
    {
        path: PRIVATE_ROUTES.calendar,
        canActivate: [MustChangePasswordGuard, PrivateWithRoleGuard],
        loadComponent: () => import('./pages/calendar/calendar.component').then(m => m.CalendarComponent)
    },
    {
        path: PRIVATE_ROUTES.messages,
        canActivate: [MustChangePasswordGuard, PrivateWithRoleGuard],
        loadComponent: () => import('./pages/messages/messages.component').then(m => m.MessagesComponent)
    },
    {
        path: PRIVATE_ROUTES.notifications,
        canActivate: [MustChangePasswordGuard, PrivateWithRoleGuard],
        loadComponent: () => import('./pages/notifications/notifications.component').then(m => m.NotificationsComponent)
    },
    {
        path: PRIVATE_ROUTES.profile,
        canActivate: [MustChangePasswordGuard, PrivateWithRoleGuard, ReceptionistBlockedGuard],
        loadComponent: () => import('./pages/profile/profile.component').then(m => m.ProfileComponent)
    },
    {
        path: PRIVATE_ROUTES.settings,
        canActivate: [MustChangePasswordGuard, PrivateWithRoleGuard],
        loadComponent: () => import('./pages/settings/settings.component').then(m => m.SettingsComponent)
    },
    {
        path: PRIVATE_ROUTES.visitorReport,
        canActivate: [MustChangePasswordGuard, PrivateWithRoleGuard],
        loadComponent: () => import('./pages/visitor-report/visitor-report.component').then(m => m.VisitorReportComponent)
    },
    {
        path: 'admin',
        canActivate: [MustChangePasswordGuard, AdminGuard],
        loadChildren: () => import('./admin/admin.routes').then(m => m.ADMIN_ROUTES),
    },

    {
        path: '',
        redirectTo: '/' + PUBLIC_ROUTES.login,
        pathMatch: 'full'
    },
    {
        path: '**',
        redirectTo: '/' + PUBLIC_ROUTES.login,
        pathMatch: 'full'
    },
    // { path: '**', component: NopagefoundComponent },
    // TODO resto de rutas
];
